import { useEffect, useState } from 'react';
import { formatJMD, formatUSD } from '../../lib/finances';
import {
  CHANNELS,
  PAYMENT_METHODS,
  discountAmount,
  invoiceNetTotal,
  invoiceTotal,
  lineTotal,
} from '../../lib/invoices';
import { todayISO } from '../../lib/dates';
import { describeError } from '../../lib/errors';
import ProductAutocomplete from './ProductAutocomplete';

const BLANK_ITEM = { description: '', product_id: null, unit_price: '', quantity: '' };

const blank = () => ({
  issue_date: todayISO(),
  due_date: '',
  // The date the goods were supplied, which is the "Date" column on the
  // printed invoice. It's one field, not one per line: on the paper invoices
  // this replaced, every row carried the same date — a per-row field meant
  // typing it three times and was the widest column in the row for no gain.
  // It's still written onto each line (that's what invoice_items.item_date
  // means), so nothing about the printed sheet changes.
  supplied_date: '',
  bill_to_name: '',
  bill_to_address: '',
  bill_to_email: '',
  currency: 'JMD',
  exchange_rate: '',
  channel: 'Wholesale',
  payment_method: 'Bank Transfer',
  // Blank, not 0: a discount is the exception, and a 0 sitting in the box
  // invites deleting it before typing, which is one keystroke of friction on
  // every invoice for the rare one that has a discount.
  discount_type: 'Percent',
  discount_value: '',
  notes: '',
});

export default function InvoiceForm({ initialInvoice, products, onSubmit, onCancel }) {
  const [form, setForm] = useState(blank);
  const [items, setItems] = useState([{ ...BLANK_ITEM }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!initialInvoice) {
      setForm(blank());
      setItems([{ ...BLANK_ITEM }]);
      return;
    }
    const lines = initialInvoice.invoice_items || [];
    setForm({
      issue_date: initialInvoice.issue_date,
      due_date: initialInvoice.due_date || '',
      // Every line carries the same supplied date, so the first one is it.
      supplied_date: lines.find((i) => i.item_date)?.item_date || '',
      bill_to_name: initialInvoice.bill_to_name || '',
      bill_to_address: initialInvoice.bill_to_address || '',
      bill_to_email: initialInvoice.bill_to_email || '',
      currency: initialInvoice.currency,
      exchange_rate: initialInvoice.currency === 'USD' ? String(initialInvoice.exchange_rate) : '',
      channel: initialInvoice.channel || 'Wholesale',
      payment_method: initialInvoice.payment_method || 'Bank Transfer',
      discount_type: initialInvoice.discount_type || 'Percent',
      discount_value: Number(initialInvoice.discount_value) ? String(initialInvoice.discount_value) : '',
      notes: initialInvoice.notes || '',
    });
    setItems(
      lines.map((i) => ({
        description: i.description,
        product_id: i.product_id,
        unit_price: String(i.unit_price),
        quantity: String(i.quantity),
      }))
    );
  }, [initialInvoice]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const setItem = (index, patch) =>
    setItems((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const addRow = () => setItems((rows) => [...rows, { ...BLANK_ITEM }]);
  const removeRow = (index) =>
    setItems((rows) => (rows.length === 1 ? [{ ...BLANK_ITEM }] : rows.filter((_, i) => i !== index)));

  const filled = items.filter((i) => i.description.trim());
  const subtotal = invoiceTotal(filled);
  // Built from the same two functions the printed sheet and the books use, so
  // the figure agreed with the customer here is the figure everywhere else.
  const discount = discountAmount(form, filled);
  const total = invoiceNetTotal(form, filled);
  const rate = Number(form.exchange_rate) || 0;
  const money = form.currency === 'USD' ? formatUSD : formatJMD;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bill_to_name.trim()) {
      setError('Who is this invoice for?');
      return;
    }
    if (filled.length === 0) {
      setError('An invoice needs at least one line.');
      return;
    }
    // Every other money table in this app stores the amount in its own
    // currency plus the rate, so a USD invoice without a rate can't be
    // converted for the books at all.
    if (form.currency === 'USD' && !(rate > 0)) {
      setError('Give the USD exchange rate used, so this lands in the books in JMD.');
      return;
    }
    // The database has the same check. Catching it here says which field is
    // wrong instead of surfacing a constraint name.
    if (form.discount_type === 'Percent' && Number(form.discount_value) > 100) {
      setError('A discount can’t be more than 100%.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(
        {
          issue_date: form.issue_date,
          due_date: form.due_date || null,
          bill_to_name: form.bill_to_name.trim(),
          bill_to_address: form.bill_to_address.trim() || null,
          bill_to_email: form.bill_to_email.trim() || null,
          currency: form.currency,
          exchange_rate: form.currency === 'USD' ? rate : 1,
          channel: form.channel || null,
          payment_method: form.payment_method || null,
          // No discount is stored as a null type and a zero, not as a type
          // with nothing behind it — so "10%" left over from an edit that
          // cleared the value can't quietly reapply itself later.
          discount_type: Number(form.discount_value) > 0 ? form.discount_type : null,
          discount_value: Number(form.discount_value) || 0,
          notes: form.notes.trim() || null,
        },
        // The one supplied date is stamped onto every line, because that is
        // what invoice_items.item_date means and what the printed Date column
        // renders from. Blank falls back to the invoice date at print time.
        filled.map((item) => ({ ...item, item_date: form.supplied_date || null }))
      );
    } catch (err) {
      setError(describeError(err, initialInvoice ? 'Save invoice' : 'Create invoice'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="invoice-form" onSubmit={handleSubmit}>
      <div className="invoice-form-grid">
        <label className="field">
          <span>Bill to</span>
          <input value={form.bill_to_name} onChange={set('bill_to_name')} placeholder="Customer name" />
        </label>
        <label className="field">
          <span>Address</span>
          <input
            value={form.bill_to_address}
            onChange={set('bill_to_address')}
            placeholder="Kingston, Jamaica"
          />
        </label>
        <label className="field">
          <span>Email</span>
          <input value={form.bill_to_email} onChange={set('bill_to_email')} placeholder="Optional" />
        </label>
        <label className="field">
          <span>Invoice date</span>
          <input type="date" value={form.issue_date} onChange={set('issue_date')} />
        </label>
        <label className="field">
          <span>Due date</span>
          <input type="date" value={form.due_date} onChange={set('due_date')} />
        </label>
        <label className="field">
          <span>Goods supplied</span>
          <input type="date" value={form.supplied_date} onChange={set('supplied_date')} />
        </label>
        <label className="field">
          <span>Sale type</span>
          <select value={form.channel} onChange={set('channel')}>
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Payment method</span>
          <select value={form.payment_method} onChange={set('payment_method')}>
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Currency</span>
          <select value={form.currency} onChange={set('currency')}>
            <option value="JMD">JMD</option>
            <option value="USD">USD</option>
          </select>
        </label>
        {form.currency === 'USD' && (
          <label className="field">
            <span>Exchange rate</span>
            <input
              type="number"
              step="0.01"
              value={form.exchange_rate}
              onChange={set('exchange_rate')}
              placeholder="JMD per US$1"
            />
          </label>
        )}
      </div>

      <div className="invoice-lines">
        <div className="invoice-lines-head">
          <span>Item description</span>
          <span>Price</span>
          <span>Qty</span>
          <span>Total</span>
          <span />
        </div>

        {items.map((item, index) => (
          <div className="invoice-line" key={index}>
            <ProductAutocomplete
              value={item.description}
              products={products}
              placeholder="Type, or pick from your products"
              // Typing after picking breaks the link on purpose: the line no
              // longer describes that product, so its stock must not move.
              onChange={(description) => setItem(index, { description, product_id: null })}
              onPick={(product) =>
                setItem(index, {
                  description: product.name,
                  product_id: product.id,
                  // Only fills a blank price — a discount already typed in
                  // shouldn't be overwritten by the list price.
                  unit_price: item.unit_price || (product.price_jmd ?? ''),
                })
              }
            />
            <input
              type="number"
              step="0.01"
              min="0"
              value={item.unit_price}
              onChange={(e) => setItem(index, { unit_price: e.target.value })}
              aria-label={`Line ${index + 1} price`}
            />
            <input
              type="number"
              step="1"
              min="0"
              value={item.quantity}
              onChange={(e) => setItem(index, { quantity: e.target.value })}
              aria-label={`Line ${index + 1} quantity`}
            />
            <span className="invoice-line-total">{money(lineTotal(item))}</span>
            <button
              type="button"
              className="invoice-line-remove"
              onClick={() => removeRow(index)}
              aria-label={`Remove line ${index + 1}`}
            >
              ×
            </button>
          </div>
        ))}

        <button type="button" className="ghost-button" onClick={addRow}>
          + Add line
        </button>
      </div>

      {/* Sits under the lines and above Notes, because that's where it
          happens: the lines are agreed first, then something comes off the
          bottom. It was previously typed into Notes as a sentence, which left
          the printed Total contradicting it. */}
      <div className="invoice-discount">
        <span className="invoice-discount-label">Discount</span>
        <select
          value={form.discount_type}
          onChange={set('discount_type')}
          aria-label="Discount type"
        >
          <option value="Percent">% of subtotal</option>
          <option value="Amount">{form.currency} off</option>
        </select>
        <input
          type="number"
          step="0.01"
          min="0"
          max={form.discount_type === 'Percent' ? 100 : undefined}
          value={form.discount_value}
          onChange={set('discount_value')}
          placeholder="0"
          aria-label="Discount amount"
        />
        {discount > 0 && <span className="invoice-discount-preview">− {money(discount)}</span>}
      </div>

      <label className="field">
        <span>Notes</span>
        <input value={form.notes} onChange={set('notes')} placeholder="Optional, printed on the invoice" />
      </label>

      <div className="invoice-form-footer">
        <div className="invoice-form-total">
          {/* The subtotal and the discount only appear once there is one —
              an invoice with nothing off it should read as a single number,
              not a three-line statement of arithmetic that didn't happen. */}
          {discount > 0 && (
            <span className="invoice-form-breakdown">
              {money(subtotal)} less {money(discount)}
              {form.discount_type === 'Percent' && ` (${Number(form.discount_value)}%)`}
            </span>
          )}
          <span>Total</span>
          <strong>{money(total)}</strong>
          {form.currency === 'USD' && rate > 0 && (
            <span className="invoice-form-converted">= {formatJMD(total * rate)}</span>
          )}
        </div>
        <div className="invoice-form-actions">
          <button type="button" className="ghost-button" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="primary-action" disabled={submitting}>
            {submitting ? 'Saving…' : initialInvoice ? 'Save invoice' : 'Create invoice'}
          </button>
        </div>
      </div>

      {error && <p className="po-error">{error}</p>}
    </form>
  );
}
